'use client'

import { useTranslate } from '@tolgee/react'
import { MouseEvent, ReactNode } from 'react'

import { P } from '@/components/common/Typo'
import IconGlobe from '@/components/icons/IconGlobe'
import Link from 'next/link'
import { getOmsLocaleLabel } from '@/libs/tolgee/constants'
import { HoverPrefetchLink } from '@/components/common/Link'
import { useRouter } from '@/libs/tolgee/navigation'

// Default back link - you may need to adjust this
const ODDLE_BACK_LINK = 'https://oddle.me'

const MAX_LOCALE_OPTIONS = 2

interface BasicContentProps {
  previewMode: boolean
  locales: string[]
  locale: string
}

const BasicContent = ({
  previewMode,
  locales,
  locale,
}: BasicContentProps): ReactNode => {
  const { t } = useTranslate()
  const splitLocale = locale.split('_')
  const oddleBackLink = `${ODDLE_BACK_LINK}/${splitLocale?.[1]?.toLocaleLowerCase()}`
  const router = useRouter()

  const handleOnClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!previewMode) return
    e.preventDefault()
    if (
      window.confirm(
        t(
          'This link cannot be opened in customisation preview. It will be opened in a new window ({target}).\nClick OK to continue',
          { target: oddleBackLink }
        )
      )
    ) {
      window.open(oddleBackLink, '_blank')
    }
  }

  const handleLocaleOnClick = () => {
    // TODO: Implement language selection modal or dropdown
    console.log('Language selection clicked')
  }

  const generateLocalesOptions = (
    locales: string[],
    buttonText: string,
    onClick: () => void
  ) => {
    const section: ReactNode[] = []

    for (let i = 0; i < locales.length; i++) {
      if (i > 0) {
        section.push(
          <span
            key={`d-${i}`}
            className="mx-8 h-3 w-px border-l border-current md:mx-2"
          />
        )
      }

      if (i === MAX_LOCALE_OPTIONS && locales.length > MAX_LOCALE_OPTIONS + 1) {
        section.push(
          <button
            key={`b-${i}`}
            onClick={onClick}
            className="text-current hover:opacity-80"
          >
            {buttonText}
          </button>
        )
        break
      }

      section.push(
        <Link
          key={`a-${i}`}
          href={`/${locales[i]}`}
          className="text-current no-underline hover:opacity-80"
        >
          {getOmsLocaleLabel(locales[i] as string)}
        </Link>
      )
    }

    return section
  }

  return (
    <div className="flex flex-col items-center justify-center pb-8 pt-8 md:flex-row md:justify-between md:pb-8">
      <div className="flex flex-col items-center md:flex-row md:items-start">
        <div className="flex items-center">
          <IconGlobe className="text-body" />
          <P className="mb-0 ml-2 mr-2 mt-0">{t('Page offered in:')}</P>
        </div>
        <div className="mt-2 flex items-center justify-center md:mt-0">
          <P className="contents">
            {generateLocalesOptions(
              locales,
              t('Show more'),
              handleLocaleOnClick
            )}
          </P>
        </div>
      </div>

      <div className="mb-2 mt-5 flex flex-col items-center gap-1.5 md:mb-0 md:ml-auto md:mt-0 md:flex-row md:items-end md:items-baseline">
        <div className="flex flex-row">
          <div
            onClick={() => router.push('/terms')}
            className="text-current no-underline hover:opacity-80 md:mx-2"
          >
            <P className="mx-2.5">{t('Terms & Conditions')}</P>
          </div>
          <div
            onClick={() => router.push('/privacy')}
            className="text-current no-underline hover:opacity-80 md:mx-2"
          >
            <P className="mx-2.5">{t('Privacy Policy')}</P>
          </div>
        </div>
        <div className="mb-1.5 ml-0 flex items-baseline md:mb-0 md:ml-2">
          <P className="mr-1">{t('Powered by')}</P>
          <HoverPrefetchLink
            href={oddleBackLink || '#'}
            onClick={handleOnClick}
          >
            <P className="font-bold">{t('Oddle.me')}</P>
          </HoverPrefetchLink>
        </div>
      </div>
    </div>
  )
}

export default BasicContent
